export function bookingStatusLabel(status: string): string {
    if (['completed', 'confirmed'].includes(status)) return 'Confirmed';
    if (status === 'cancelled') return 'Cancelled';
    if (status === 'refunded') return 'Refunded';
    if (['failed', 'payment_failed', 'order_failed', 'booking_failed', 'rejected', 'refund_pending'].includes(status)) return 'Failed';
    return 'Pending';
}

export function paymentStatusLabel(status?: string | null): string {
    if (!status || status === 'unknown') return 'Not recorded';
    if (status === 'paid' || status === 'successful' || status === 'captured') return 'Completed';
    if (status === 'failed') return 'Failed';
    if (status === 'refund_pending') return 'Refund pending';
    if (status === 'refunded') return 'Refunded';
    return 'Pending';
}

export type BookingDetailState = 'confirmed' | 'confirmedPaymentUnknown' | 'paidFailed' | 'paymentFailed' | 'cancelled' | 'pending' | 'unresolved';

export function paymentWasReceived(status?: string | null, hasPaidAmount = false): boolean {
    return ['paid', 'successful', 'captured', 'refund_pending', 'refunded'].includes(status ?? '') || hasPaidAmount;
}

export function bookingReference(orderId: number, status: string): string | null {
    return Number.isFinite(orderId) && orderId > 0 && ['completed', 'confirmed', 'cancelled', 'refunded'].includes(status)
        ? `SS-${orderId}` : null;
}

export function bookingDetailState(bookingStatus: string, paymentStatus?: string | null, hasPaidAmount = false, cancelledLocally = false): BookingDetailState {
    if (cancelledLocally || ['cancelled', 'refunded'].includes(bookingStatus)) return 'cancelled';
    if (['refund_pending', 'refunded'].includes(paymentStatus ?? '')) return 'paidFailed';
    if (['completed', 'confirmed'].includes(bookingStatus)) return paymentWasReceived(paymentStatus, hasPaidAmount) ? 'confirmed' : 'confirmedPaymentUnknown';
    if (paymentStatus === 'failed') return 'paymentFailed';
    const bookingFailed = ['failed', 'order_failed', 'booking_failed', 'rejected', 'refund_pending'].includes(bookingStatus);
    const paymentReceived = paymentWasReceived(paymentStatus, hasPaidAmount);
    if (bookingFailed) return paymentReceived ? 'paidFailed' : 'unresolved';
    if (bookingStatus === 'payment_failed') return 'unresolved';
    return 'pending';
}

// Supplier room names sometimes include bed information in parentheses.
// Only split it out when the wording explicitly mentions a bed; do not
// manufacture a bed type from a generic room name.
export function roomAndBed(roomName: string): { room: string; bed: string | null } {
    const groups = [...roomName.matchAll(/\(([^)]+)\)/g)];
    const bedGroups = groups.filter((match) => /\bbed\b|\bking\b|\bqueen\b|\btwin\b|\bdouble\b/i.test(match[1]));
    if (!bedGroups.length) return { room: roomName, bed: null };
    const room = bedGroups.reduce((name, match) => name.replace(match[0], ''), roomName).replace(/\s+/g, ' ').trim();
    return { room: room || roomName, bed: bedGroups.map((match) => match[1]).join(' · ') };
}
